import React from "react";
import reactDom from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { DrawerArrowRightIcon, DrawerArrowLeftIcon } from "wm-graphics";
import getTestIDs from "../../../utils/getTestIDs";
import { withTranslations } from "../../../utils/withTranslations";
import { Button } from "../Button/Button";
import defaultLocales from "./locales";
import "./Drawer.scss";

const HOVER_DELAY = 550;

export const testIDs = getTestIDs();

class Drawer extends React.Component {
  static defaultProps = {
    orientation: "left",
  };

  static propTypes = {
    /** Additional class names for the component */
    className: PropTypes.string,
    /** Orientation of tooltip */
    orientation: PropTypes.string,
    /** Locales object for rendering text */
    locales: PropTypes.object,
    /** Location in the Dom to display drawer */
    location: PropTypes.object.isRequired,
    /** Whether the drawer is expanded or not */
    isExpanded: PropTypes.bool.isRequired,
    /** Additional children components to be rendered */
    children: PropTypes.node,
    /** callback function that the parent compnonent can use to mimic the components isExpanded state */
    setIsExpanded: PropTypes.func,
    /** ClassName for arrow gradient */
    arrowClassName: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      isHovered: false,
      isExpanded: props.isExpanded,
    };

    this.hoverTimeout = null;
  }

  componentDidUpdate(prevProps) {
    const { isExpanded } = this.props;
    if (isExpanded !== prevProps.isExpanded) {
      this.setIsExpanded(isExpanded);
    }
  }

  setIsExpanded(isExpanded) {
    const { setIsExpanded } = this.props;
    if (setIsExpanded) {
      setIsExpanded(isExpanded);
    }

    this.setState({ isExpanded });
  }

  onArrowClick = () => {
    const { isExpanded } = this.state;

    const newIsExpanded = !isExpanded;

    this.setIsExpanded(newIsExpanded);

    if (!newIsExpanded) {
      this.setState({ isHovered: false });
    }
  };

  renderOutOfDom = (children) => {
    const { location } = this.props;

    return reactDom.createPortal(children, location);
  };

  renderInDom = (children) => children;

  clearHoverTimeout = () => {
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = null;
  };

  handleMouseOver = () => {
    if (this.hoverTimeout === null) {
      this.hoverTimeout = setTimeout(() => {
        this.setState({ isHovered: true });
      }, HOVER_DELAY);
    }
  };

  handleMouseLeave = () => {
    this.setState({ isHovered: false });
    this.clearHoverTimeout();
  };

  handleMouseOverArrow = (event) => {
    event.stopPropagation();
    event.preventDefault();
    this.clearHoverTimeout();
  };

  render() {
    const { arrowClassName, children, className, locales, orientation } =
      this.props;

    const { isExpanded, isHovered } = this.state;

    const drawerClass = classNames("react-drawer", className, {
      open: isExpanded,
      expand: isHovered,
    });

    const drawerArrowClassName = classNames("drawer-arrow", arrowClassName);

    let renderFunction;
    let tooltipMessage;

    if (isExpanded) {
      renderFunction = this.renderInDom;
      tooltipMessage = locales.collapse;
    } else {
      renderFunction = this.renderOutOfDom;
      tooltipMessage = locales.expand;
    }
    const tooltipOrientation = orientation === "left" ? "right" : "left";

    return renderFunction(
      <div
        data-testid={testIDs.drawer}
        className={drawerClass}
        onMouseOver={this.handleMouseOver}
        onMouseLeave={this.handleMouseLeave}
        onFocus={() => undefined}
      >
        <div
          onMouseOver={this.handleMouseOverArrow}
          onFocus={() => undefined}
          className={drawerArrowClassName}
        >
          <Button
            tooltipMessage={tooltipMessage}
            tooltipDirection={tooltipOrientation}
            round
            onClick={this.onArrowClick}
          >
            <div className="Filter__Arrow">
              {!isExpanded ? (
                <DrawerArrowRightIcon width="28" height="28" />
              ) : (
                <DrawerArrowLeftIcon width="28" height="28" />
              )}
            </div>
          </Button>
        </div>
        <div className="react-drawer__children">{children}</div>
      </div>
    );
  }
}

const TranslatedDrawer = withTranslations(defaultLocales)(Drawer);

export { TranslatedDrawer as Drawer };

export default TranslatedDrawer;
